/* eslint-disable import/no-cycle */
import React from 'react';
import {useTranslation} from 'react-i18next';
import {observer} from 'mobx-react-lite';
import {groupBy} from 'lodash';
import {Divider} from 'antd';
import moment from 'moment';
import {datetimeFormat, detailedDurationFormat} from '../../config/moment';
import Table from '../shared/tables/Table';
import {sortAlphabetically, sortChronologically} from '../shared/tables/sorters';
import Descriptions from '../shared/descriptions/Descriptions';
import Label from '../shared/dataDisplay/Label';
import {EnDash} from '../shared/unicodeWrapper/EnDash';
import {useStore} from '../../hooks/useStore';
import {useMount} from '../../hooks/useMount';

const OrderDetailsInterruptions = ({operations, columns, sortAttribute, sortOrder}) => {
  const {t} = useTranslation();
  const store = useStore();
  let total = [0, 0];

  const interruptionReasons = store.interruptionReasonStore.interruptionReasons.slice()
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .sort((a, b) => a.reasonSource.name.localeCompare(b.reasonSource.name));
  const interruptions = store.operationStateLogStore.getInterruptionsByOperationIds(
    operations.map((operation) => operation.id)
  );
  const groupedInterruptions = groupBy(interruptions, (interruption) => interruption.interruptionReasonId);
  const interruptionCount = new Map();
  const interruptionDuration = new Map();
  Object.keys(groupedInterruptions).forEach((interruptionReasonId) => {
    let count = 0;
    let duration = 0;
    groupedInterruptions[interruptionReasonId].forEach((interruption) => {
      count++; // eslint-disable-line no-plusplus
      duration += moment(interruption.end).diff(moment(interruption.start), 'seconds');
    });
    // unclassified have no interruptionReasonId, set it to -1
    interruptionCount.set(parseInt(interruptionReasonId, 10) || -1, count);
    interruptionDuration.set(parseInt(interruptionReasonId, 10) || -1, duration);
    total = [total[0] + count, total[1] + duration];
  });

  useMount(() => {
    if (operations?.length) {
      store.interruptionReasonSourceStore.loadAll();
      store.interruptionReasonStore.loadAll();
      store.operationStateLogStore.loadByOperation(operations.map((operation) => operation.id));
    }
  }, [operations.reduce((res, op) => `${res}-${op.id}`, '')]);

  return (
    <>
      <Divider orientation="left">
        {t('orderWidget.interruptions.summarize')}
        <span style={{marginLeft: '25px', marginRight: '25px'}}>-</span>
        {t('orderWidget.interruptions.total')}
        <span style={{marginRight: '10px'}}>:</span>
        {total[0]}
        {' / '}
        {moment.utc(total[1] * 1000).format(detailedDurationFormat)}
      </Divider>
      <Descriptions column={columns} bordered>
        {interruptionReasons.map((reason) => interruptionCount.get(reason.id) && (
          <Descriptions.Item
            key={reason.id}
            label={<Label>{reason.reasonSource.name} > {reason.name}</Label>} // eslint-disable-line
          >
            {interruptionCount.get(reason.id) || 0}
            {' / '}
            {moment.utc(interruptionDuration.get(reason.id) * 1000 || 0).format(detailedDurationFormat)}
          </Descriptions.Item>
        ))}
        {interruptionCount.get(-1) && (
        <Descriptions.Item
          key="unclassified"
          label={<Label>{t('orderWidget.interruptions.unclassified')}</Label>}
        >
          {interruptionCount.get(-1) || 0}
          {' / '}
          {moment.utc(interruptionDuration.get(-1) * 1000 || 0).format(detailedDurationFormat)}
        </Descriptions.Item>
        )}
        {interruptionCount.size === 0
        && <div>{EnDash()}</div>}
      </Descriptions>
      <Divider orientation="left">{t('orderWidget.interruptions.details')}</Divider>
      <Table
        columns={[
          {
            title: t('operationStateLog.model.attributes.start'),
            dataIndex: 'start',
            render: (date) => moment(date).format(datetimeFormat),
            sorter: (a, b) => sortChronologically(a.start, b.start),
            sortOrder: (sortAttribute === 'time') ? sortOrder : false,
          },
          {
            title: t('operationStateLog.model.attributes.durationSeconds'),
            dataIndex: 'interruptionDuration',
            render: (duration) => moment.utc(duration * 1000).format(detailedDurationFormat),
            sorter: (a, b) => sortChronologically(a.interruptionDuration, b.interruptionDuration),
            sortOrder: (sortAttribute === 'duration') ? sortOrder : false,
          },
          {
            title: t('interruptionReason.model.one'),
            dataIndex: 'interruptionReason',
            render: (reason) => reason && `${reason?.reasonSource?.name}  > ${reason?.name}`,
            sorter: (a, b) => sortAlphabetically(a.interruptionReason?.name, b.interruptionReason?.name),
          },
          {
            title: t('operationStateLog.model.attributes.message'),
            dataIndex: 'message',
            sorter: (a, b) => sortAlphabetically(a.message, b.message),
          },
        ]}
        dataSource={interruptions || []}
        rowKey={'id'}
      />

    </>
  );
};

export default observer(OrderDetailsInterruptions);
